import { CoreEntityType } from '#/index';
import { povOptions } from '@/constants';
import { PointOfView, PromptFunction, ExecuteParams, ExecutionContext, TextContainer, ListContainer } from '../../types';
import PointOfViewUtils from '../../utils/pointOfView';
import { createCustomFunction } from './createFunction';


const getPointOfViewByElements = (context: ExecutionContext, elements: ExecuteParams[]): PointOfView[] => {
  const pov = new PointOfViewUtils(context);
  elements.forEach(element => {
    if (typeof element === 'object' && element.type === 'outlineTree') {
      pov.byOutline(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.Novels) {
      pov.byNovel(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelActs) {
      pov.byAct(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelChapters) {
      pov.byChapter(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelScenes) {
      pov.byScene(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexEntries) {
      pov.byCodexEntry(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexCategories) {
      pov.byCodexCategory(element)
    } else if (typeof element === 'object' && element.type === 'codexDetail') {
      pov.byCodexDetail(element)
    } else if (typeof element === 'object' && element.type === 'novelLabel') {
      pov.byLabel(element)
    } else if (typeof element === 'object' && element.type === 'textContainer') {
      if (element.reference) {
        getPointOfViewByElements(context, [element.reference])
      }
    } else if (typeof element === 'object' && element.type === 'listContainer') {
      getPointOfViewByElements(context, element.elements).forEach(d => {
        pov.byPov(d)
      })
    }
  })
  return pov.getResult();
}

const contextFunctions: Record<string, PromptFunction> = {
  pov: createCustomFunction(
    'pov',
    '从给定参数中提取所有可用的POV视角，或从当前场景中提取POV视角。',
    (context, ...params: ExecuteParams[]): ListContainer<PointOfView> | void => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      if (!result?.length) return;
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  'pov.character': createCustomFunction(
    'pov.character',
    '从给定/当前视点提取角色名称。',
    (context, ...params: ExecuteParams[]): ListContainer<TextContainer> => {
      const { codexEntries } = context.globalContext.generateStore.getState();
      let result: PointOfView[] = getPointOfViewByElements(context, params)
      return {
        type: 'listContainer',
        elements: result.map(item => {
          const entry = item.characterId ? codexEntries.find(entry => entry.id === item.characterId) : undefined;
          return {
            type: 'textContainer',
            reference: item,
            title: entry?.attributes.name,
            defaultFormat: 'plain',
            formatters: {
              plain: (container) => container.title,
            },
          } as TextContainer
        }).filter(d => d.title),
        defaultFormat: 'inherited',
      }
    }
  ),
  'pov.isFirstPerson': createCustomFunction(
    'pov.isFirstPerson',
    '返回作为第一人称的所有给定（或当前）POV视角。',
    (context, ...params: ExecuteParams[]): ListContainer<PointOfView> => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result.filter(item => item.value === 'firstPerson'),
        defaultFormat: 'inherited',
      }
    }
  ),
  'pov.isLimited': createCustomFunction(
    'pov.isLimited',
    '返回作为第三人称有限POV视角的所有给定（或当前）POV视角。',
    (context, ...params: ExecuteParams[]): ListContainer<PointOfView> => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result.filter(item => item.value === 'thirdPersonLimited'),
        defaultFormat: 'inherited',
      }
    }
  ),
  'pov.isOmniscient': createCustomFunction(
    'pov.isOmniscient',
    '返回作为第三人称全知POV视角的所有给定（或当前）POV视角。',
    (context, ...params: ExecuteParams[]): ListContainer<PointOfView> => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result.filter(item => item.value === 'thirdPersonOmniscient'),
        defaultFormat: 'inherited',
      }
    },
  ),
  'pov.isOverwrite': createCustomFunction(
    'pov.isOverwrite',
    '返回与小说默认POV视角不同的所有给定（或当前）POV视角。',
    (context, ...params: ExecuteParams[]): ListContainer<PointOfView> => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result.filter(item => item.value !== context.globalContext.currentPosition?.novel?.attributes.pov?.type),
        defaultFormat: 'inherited',
      }
    }
  ),
  'pov.isSecondPerson': createCustomFunction(
    'pov.isSecondPerson',
    '返回作为第二人称的所有给定（或当前）POV视角。',
    (context, ...params: ExecuteParams[]): ListContainer<PointOfView> => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result.filter(item => item.value === 'secondPerson'),
        defaultFormat: 'inherited',
      }
    },
  ),
  'pov.isThirdPerson': createCustomFunction(
    'pov.isThirdPerson',
    '返回作为第三人称的所有给定（或当前）POV视角。',
    (context, ...params: ExecuteParams[]): ListContainer<PointOfView> => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result.filter(item => item.value === 'thirdPerson'),
        defaultFormat: 'inherited',
      }
    },
  ),
  'pov.type': createCustomFunction(
    'pov.type',
    '从给定/当前视点提取POV视角类型。',
    (context, ...params: ExecuteParams[]): ListContainer<TextContainer> => {
      let result: PointOfView[] = []
      if (params.length) {
        result = getPointOfViewByElements(context, params)
      } else {
        const { scene, novel } = context.globalContext.currentPosition || {};
        // 当前场景POV视角
        if (scene?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov.characterId
          })
        }
        if (novel?.attributes.pov) {
          result.push({
            type: 'pointOfView',
            value: novel.attributes.pov.type,
            characterId: novel.attributes.pov.characterId
          })
        }
      }
      return {
        type: 'listContainer',
        elements: result.map(item => {
          const povOption = povOptions.find(d => d.id === item.value);
          return {
            type: 'textContainer',
            reference: item,
            title: povOption?.label,
            defaultFormat: 'plain',
            formatters: {
              plain: (container) => container.title,
            },
          } as TextContainer
        }).filter(d => d.title),
        defaultFormat: 'inherited',
      }
    },
  )
}
export default contextFunctions;